<template>
  <div class="container">
    <Loading v-show="pageLoading" />
    <div class="width_wrap">
      <MyPageSidebar />
      <div class="body_content sub">
        <!-- 서브 타이틀영역 -->
        <h1 class="sub_subject">기관 사용자 관리<span></span></h1>
        <div class="glores-B-list-h2">
          <div>
            <n-descriptions
              v-if="organizationInfo"
              title="기관 정보"
              label-placement="left"
              :column="2"
              size="small"
              bordered
              class="org-info-descriptions"
              style="margin-bottom: 10px"
            >
              <n-descriptions-item label="기관 ID">{{
                organizationInfo.uid || "N/A"
              }}</n-descriptions-item>
              <n-descriptions-item label="기관명">{{
                organizationInfo.name || "N/A"
              }}</n-descriptions-item>
              <n-descriptions-item label="대표자 이름">{{
                organizationInfo.president || "N/A"
              }}</n-descriptions-item>
              <n-descriptions-item label="기관 유형">{{
                common.getOrganizationTypeString(
                  organizationInfo.type as OrganizationType
                )
              }}</n-descriptions-item>
            </n-descriptions>
            <div v-else-if="!pageLoading && !listLoading">
              <!-- Show only if not loading and org info is indeed not found -->
              <n-alert
                title="정보 없음"
                type="info"
                :bordered="false"
                style="margin-bottom: 10px"
              >
                기관 정보를 표시할 수 없습니다.
              </n-alert>
            </div>
          </div>
          <div class="h-left">
            <!-- 필요시 여기에 검색 또는 필터 UI 추가 -->
          </div>
          <div
            class="h-right"
            style="display: flex; flex-direction: column; align-items: flex-end"
          >
            <n-button
              v-if="isOrgAdmin"
              type="primary"
              @click="openAddUserModal"
              style="margin-bottom: 8px"
            >
              사용자 추가
            </n-button>
          </div>
        </div>
        <div>사용자 목록</div>
        <div
          v-if="listLoading"
          class="loading-indicator"
          style="text-align: center; padding: 20px"
        >
          사용자 목록을 불러오는 중...
        </div>
        <div
          v-if="error"
          class="error-message"
          style="text-align: center; padding: 20px; color: red"
        >
          {{ error }}
        </div>

        <div v-if="!listLoading && !error" class="glores-B-list-type1">
          <n-data-table
            v-if="orgUsers.length > 0"
            :columns="columns"
            :data="orgUsers"
            :pagination="pagination"
            :bordered="false"
            :single-line="false"
          />
          <ul v-else>
            <li class="none-data">등록된 사용자가 없습니다.</li>
          </ul>
        </div>

        <div class="glores-B-paginate">
          <div class="ui-pagenate"></div>
        </div>
      </div>
    </div>
    <n-modal
      v-model:show="showUserDetailModal"
      preset="card"
      style="width: 800px; max-width: 90vw; max-height: 700px"
      title="사용자 상세 정보"
      :bordered="true"
      :closable="!isSavingDetail"
      :mask-closable="!isSavingDetail"
      @after-leave="selectedUserDetail = null"
    >
      <div
        v-if="selectedUserDetail"
        class="order-detail-modal-content"
        style="overflow-y: auto; max-height: calc(700px - 150px); padding-right: 5px"
      >
        <!-- 조회 모드 -->
        <n-descriptions
          v-if="!isEditingDetail"
          label-placement="left"
          bordered
          :column="2"
          size="small"
        >
          <n-descriptions-item label="아이디">
            {{ selectedUserDetail.SK.split("#").pop() }}
          </n-descriptions-item>
          <n-descriptions-item label="이름">{{
            selectedUserDetail.name
          }}</n-descriptions-item>
          <n-descriptions-item label="이메일">{{
            selectedUserDetail.email || "N/A"
          }}</n-descriptions-item>
          <n-descriptions-item label="전화번호">{{
            selectedUserDetail.phoneNumber || "N/A"
          }}</n-descriptions-item>
          <n-descriptions-item label="역할">{{
            selectedUserDetail.role || "N/A"
          }}</n-descriptions-item>
          <n-descriptions-item label="직책">{{
            selectedUserDetail.position || "N/A"
          }}</n-descriptions-item>
          <n-descriptions-item label="설명">{{
            selectedUserDetail.description || "N/A"
          }}</n-descriptions-item>
        </n-descriptions>

        <!-- 수정 모드 -->
        <div v-else>
          <n-alert type="warning" title="주의 사항" style="margin-bottom: 15px">
            사용자 정보를 수정하더라도 이미 이 사용자가 담당자로 지정된 기존 진단 정보(학급 정보 등)에는 자동으로 반영되지 않습니다.
            기존 진단의 담당자 정보도 함께 업데이트하려면, 해당 진단의 상세 화면에서 '담당자 변경' 기능을 사용하여 재설정해 주시기 바랍니다.
          </n-alert>
          <n-form
            ref="editFormRef"
            :model="editFormData"
            :rules="editUserFormRules"
            label-placement="left"
            label-width="80"
          >
            <n-grid :cols="2" :x-gap="24">
              <n-form-item-gi label="아이디">
                <n-input :value="editFormData.uid" disabled />
              </n-form-item-gi>
              <n-form-item-gi path="name" label="이름">
                <n-input v-model:value="editFormData.name" placeholder="이름을 입력해주세요" :disabled="isSavingDetail" />
              </n-form-item-gi>
              <n-form-item-gi path="email" label="이메일">
                <n-input v-model:value="editFormData.email" placeholder="이메일을 입력해주세요" :disabled="isSavingDetail" />
              </n-form-item-gi>
              <n-form-item-gi label="전화번호">
                <n-input v-model:value="editFormData.phoneNumber" placeholder="전화번호를 입력해주세요" :disabled="isSavingDetail" />
              </n-form-item-gi>
              <n-form-item-gi label="역할">
                <n-input v-model:value="editFormData.role" placeholder="역할을 입력해주세요 (예: 교사, 강사)" :disabled="isSavingDetail" />
              </n-form-item-gi>
              <n-form-item-gi label="직책">
                <n-input v-model:value="editFormData.position" placeholder="직책을 입력해주세요 (예: 담임, 주임)" :disabled="isSavingDetail" />
              </n-form-item-gi>
              <n-form-item-gi label="설명" span="2">
                <n-input
                  v-model:value="editFormData.description"
                  type="textarea"
                  placeholder="설명을 입력해주세요"
                  :disabled="isSavingDetail"
                  :autosize="{ minRows: 2, maxRows: 4 }"
                />
              </n-form-item-gi>
            </n-grid>
          </n-form>
        </div>
      </div>
      <template #footer>
        <div style="width: 100%; display: flex; justify-content: center; gap: 10px">
          <!-- 조회 모드 버튼 -->
          <template v-if="!isEditingDetail">
            <n-button v-if="isOrgAdmin" type="primary" @click="isEditingDetail = true">수정</n-button>
            <n-button @click="showUserDetailModal = false">닫기</n-button>
          </template>
          <!-- 수정 모드 버튼 -->
          <template v-else>
            <n-button type="primary" :loading="isSavingDetail" @click="handleSaveUserDetail">저장</n-button>
            <n-button :disabled="isSavingDetail" @click="isEditingDetail = false">취소</n-button>
          </template>
        </div>
      </template>
    </n-modal>

    <!-- 암호 변경 모달 -->
    <n-modal
      v-model:show="showChangePasswordModal"
      preset="card"
      style="width: 500px; max-width: 90vw"
      title="암호 변경"
      :bordered="true"
      :closable="true"
      :mask-closable="false"
      @after-leave="onCloseChangePasswordModal"
    >
      <div v-if="userForPasswordChange">
        <p style="margin-bottom: 15px">
          <strong>{{ userForPasswordChange.name }}</strong> ({{
            userForPasswordChange.SK.split("#").pop()
          }}) 님의 암호를 변경합니다.
        </p>
        <n-form ref="changePasswordFormRef" :model="passwordFormData">
          <n-form-item path="oldPassword" label="기존 암호">
            <n-input
              v-model:value="passwordFormData.oldPassword"
              type="password"
              placeholder="기존 암호를 입력하세요"
              show-password-on="mousedown"
            />
          </n-form-item>
          <n-form-item path="newPassword" label="새 암호">
            <n-input
              v-model:value="passwordFormData.newPassword"
              type="password"
              placeholder="새 암호를 입력하세요"
              show-password-on="mousedown"
            />
          </n-form-item>
          <n-form-item path="confirmNewPassword" label="새 암호 확인">
            <n-input
              v-model:value="passwordFormData.confirmNewPassword"
              type="password"
              placeholder="새 암호를 다시 입력하세요"
              show-password-on="mousedown"
            />
          </n-form-item>
        </n-form>
        <n-alert
          v-if="passwordChangeError"
          title="오류"
          type="error"
          :closable="true"
          @close="passwordChangeError = null"
          style="margin-top: 10px"
          >{{ passwordChangeError }}</n-alert
        >
      </div>
      <template #footer>
        <div style="width: 100%; text-align: right">
          <n-button
            @click="showChangePasswordModal = false"
            style="margin-right: 8px"
            >취소</n-button
          >
          <n-button
            type="primary"
            @click="handlePasswordChangeSubmit"
            :loading="passwordChangeLoading"
            >변경</n-button
          >
        </div>
      </template>
    </n-modal>

    <!-- 사용자 추가 모달 -->
    <n-modal
      v-model:show="showAddUserModal"
      preset="card"
      style="width: 600px; max-width: 90vw"
      title="신규 사용자 추가"
      :bordered="true"
      :closable="true"
      :mask-closable="false"
      @after-leave="onCloseAddUserModal"
    >
      <n-form
        ref="addUserFormRef"
        :model="newUserData"
        :rules="addUserFormRules"
      >
        <n-grid :cols="12" :x-gap="24">
          <n-form-item-gi :span="6" path="uid" label="사용자 ID">
            <n-input
              v-model:value="newUserData.uid"
              placeholder="사용자 ID (로그인 시 사용)"
            />
          </n-form-item-gi>
          <n-form-item-gi :span="6" path="name" label="이름">
            <n-input
              v-model:value="newUserData.name"
              placeholder="사용자 이름"
            />
          </n-form-item-gi>
          <n-form-item-gi :span="6" path="email" label="이메일">
            <n-input
              v-model:value="newUserData.email"
              placeholder="이메일 주소 (선택)"
            />
          </n-form-item-gi>
          <n-form-item-gi :span="6" path="phoneNumber" label="전화번호">
            <n-input
              v-model:value="newUserData.phoneNumber"
              placeholder="전화번호 (선택)"
            />
          </n-form-item-gi>
          <n-form-item-gi :span="4" path="role" label="역할">
            <n-input
              v-model:value="newUserData.role"
              placeholder="역할 (선택)"
            />
          </n-form-item-gi>
          <n-form-item-gi :span="4" path="position" label="직책">
            <n-input
              v-model:value="newUserData.position"
              placeholder="직책 (선택)"
            />
          </n-form-item-gi>
          <n-form-item-gi :span="4" path="type" label="유형">
            <n-select
              v-model:value="newUserData.type"
              placeholder="사용자 유형 선택"
              :options="userTypeOptions"
            />
          </n-form-item-gi>
          <n-form-item-gi :span="12" path="description" label="설명">
            <n-input
              v-model:value="newUserData.description"
              type="textarea"
              placeholder="설명 (선택)"
              :autosize="{ minRows: 2, maxRows: 4 }"
            />
          </n-form-item-gi>
          <n-form-item-gi :span="6" path="password" label="초기 암호">
            <n-input
              v-model:value="newUserData.password"
              type="password"
              placeholder="초기 암호 설정"
              show-password-on="mousedown"
            />
          </n-form-item-gi>
          <n-form-item-gi :span="6" path="confirmPassword" label="암호 확인">
            <n-input
              v-model:value="newUserData.confirmPassword"
              type="password"
              placeholder="초기 암호 다시 입력"
              show-password-on="mousedown"
            />
          </n-form-item-gi>
        </n-grid>
      </n-form>
      <n-alert
        v-if="addUserError"
        title="오류"
        type="error"
        :closable="true"
        @close="addUserError = null"
        style="margin-top: 10px"
        >{{ addUserError }}</n-alert
      >
      <template #footer>
        <div style="width: 100%; text-align: right">
          <n-button @click="showAddUserModal = false" style="margin-right: 8px"
            >취소</n-button
          >
          <n-button
            type="primary"
            @click="handleAddUserSubmit"
            :loading="addUserLoading"
            >추가</n-button
          >
        </div>
      </template>
    </n-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, h, computed, watch } from "vue";
import MyPageSidebar from "@/views/member/MypageSidebar.vue";
import Banner from "@/components/Banner.vue";
import {
  NDataTable,
  NButton,
  NSpace,
  useMessage,
  NModal,
  NDescriptions,
  NDescriptionsItem,
  NForm,
  NFormItem,
  NInput,
  NAlert,
  FormInst,
  FormRules,
  NSelect,
  useNotification,
  NGrid,
  useDialog,
  NFormItemGi,
} from "naive-ui";
import Loading from "@/components/Loading.vue";
import { getCallable } from "@/config/env";
import * as common from "@/assets/common/common";
import * as constant from "@/constants/constant";
import { useRouter } from "vue-router";
import { ChangePassword } from "@/model/base";
import { OrgUser, Organization, OrganizationType } from "@/model/organization";
import { useAuth } from "@/composables/useAuth";
import { useActionDialog } from "@/composables/useDialog";
import { useAlertDialog } from "@/composables/useAlert";
import { USER_ROUTES } from "@/assets/route_constants";
const useAlert = useAlertDialog();
const { show: showActionDialog } = useActionDialog();

const notification = useNotification();
const { isLoggedIn, isOrgAdmin } = useAuth();
const dialog = useDialog();

const message = useMessage();
const router = useRouter();
watch(
  isLoggedIn,
  (newValue) => {
    if (newValue === false) {
      router.push({ name: "Home" });
    }
  },
  { immediate: true }
);

const pageLoading = ref(false);
const listLoading = ref(false);
const error = ref<string | null>(null);
const orgUsers = ref<OrgUser[]>([]);
const organizationInfo = ref<Partial<Organization> | null>(null);

const showUserDetailModal = ref(false);
const selectedUserDetail = ref<OrgUser | null>(null);
const getOrgMembersCallable = getCallable("getOrgMembers");
const deleteOrgUserMemberCallable = getCallable("deleteOrgUserMember");

// 상세 정보 수정 관련 상태
const isEditingDetail = ref(false);
const isSavingDetail = ref(false);
const editFormRef = ref<any>(null);
const updateOrgUserCallable = getCallable("updateOrgUser");
const editFormData = ref<Partial<OrgUser>>({
  orgId: "",
  uid: "",
  name: "",
  email: "",
  phoneNumber: "",
  role: "",
  position: "",
  description: "",
});

const editUserFormRules = {
  name: [
    { required: true, message: "이름을 입력해주세요", trigger: "blur" }
  ],
  email: [
    {
      type: "email",
      message: "올바른 이메일 형식이 아닙니다",
      trigger: ["input", "blur"],
    }
  ]
};


// 암호 변경 관련 상태
const showChangePasswordModal = ref(false);
const userForPasswordChange = ref<OrgUser | null>(null);
const passwordFormData = ref({
  oldPassword: "",
  newPassword: "",
  confirmNewPassword: "",
});
const passwordChangeLoading = ref(false);
const passwordChangeError = ref<string | null>(null);
const changePasswordFormRef = ref<FormInst | null>(null);
const changeOrgMemberPasswordCallable = getCallable("changeOrgUserPassword");

interface UserUIData extends OrgUser {
  confirmPassword: string;
}

const showAddUserModal = ref(false);
const newUserData = ref<UserUIData>({
  uid: "",
  name: "",
  email: "",
  phoneNumber: "",
  role: "",
  position: "",
  password: "",
  confirmPassword: "",
  description: "",
});
const addUserLoading = ref(false);
const addUserError = ref<string | null>(null);
const addUserFormRef = ref<FormInst | null>(null);
const addOrgMemberCallable = getCallable("addOrgUser"); // 백엔드 함수명에 맞게 수정 필요

// 사용자 유형 선택 옵션
const userTypeOptions = [
  { label: "기관 관리자", value: "admin" },
  { label: "기관 사용자", value: "user" },
];

const columns = computed(() => [
  {
    title: "ID",
    key: "SK",
    render(row: OrgUser) {
      return row.SK.split("#").pop();
    },
    ellipsis: { tooltip: true },
  },
  { title: "이름", key: "name", ellipsis: { tooltip: true } },
  { title: "전화번호", key: "phoneNumber", ellipsis: { tooltip: true } },
  { title: "역할", key: "role", ellipsis: { tooltip: true } },
  { title: "이메일", key: "email", ellipsis: { tooltip: true } },
  {
    title: "관리",
    key: "actions",
    width: 300, // 버튼 두 개를 넣기 위해 너비 조정
    render(row: OrgUser) {
      const buttons: any[] = [];
      const userId = row.SK.split("#").pop();
      if (userId !== "admin") {
        buttons.push(
          h(
            NButton,
            {
              size: "small",
              type: "default",
              style: "margin-right: 6px;",
              onClick: () => viewUserDetails(row),
            },
            { default: () => "상세보기" }
          )
        );
        buttons.push(
          h(
            NButton,
            {
              size: "small",
              type: "default",
              style: "margin-right: 6px;",
              onClick: () => deleteUser(row),
            },
            { default: () => "삭제" }
          )
        );
        buttons.push(
          h(
            NButton,
            {
              size: "small",
              type: "primary",
              onClick: () => changePassword(row),
            },
            { default: () => "암호변경" }
          )
        );
      }
      return h(NSpace, { justify: "center" }, () => buttons); // NSpace로 버튼들을 감싸서 반환
    },
  },
]);

const pagination = ref({ pageSize: 10 }); // 페이지네이션 설정

// TODO : 사용자 추가 폼 유효성 검사 규칙
const addUserFormRules: FormRules = {
  uid: [
    {
      required: true,
      message: "사용자 ID를 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  name: [
    {
      required: true,
      message: "이름을 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  email: [
    {
      type: "email",
      message: "올바른 이메일 형식이 아닙니다.",
      trigger: ["input", "blur"],
      required: false,
    },
  ],
  type: [
    {
      required: true,
      message: "사용자 유형을 선택해주세요.",
      trigger: ["change", "blur"],
    },
  ],
  password: [
    {
      required: true,
      message: "초기 암호를 입력해주세요.",
      trigger: ["input", "blur"],
    },
    {
      min: 4,
      message: "암호는 최소 4자 이상이어야 합니다.",
      trigger: ["input", "blur"],
    }, // 예시: 최소 길이 규칙
  ],
  confirmPassword: [
    {
      required: true,
      message: "암호 확인을 입력해주세요.",
      trigger: ["input", "blur"],
    },
    {
      validator: (rule, value) => value === newUserData.value.password,
      message: "암호가 일치하지 않습니다.",
      trigger: ["input", "blur"],
    },
  ],
};
async function deleteUser(user: OrgUser) {
  const userId = user.SK.split("#").pop();
  const userName = user.name;

  showActionDialog({
    title: "사용자 삭제 확인",
    content: `정말로 사용자 '${userName}' (ID: ${userId})님을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`,
    positiveText: "삭제",
    negativeText: "취소",
    confirmButtonLoadingText: "삭제 중...",
    onConfirm: async () => {
      try {
        const orgId = common.getLocalAuth().orgId;
        const result = await deleteOrgUserMemberCallable({
          orgId: orgId,
          uid: userId,
        });

        if (common.evalFunctionResult(result)) {
          notification.success({
            title: "성공",
            description: `사용자 '${userName}' (ID: ${userId})님이 성공적으로 삭제되었습니다.`,
            duration: 2500,
          });
          fetchUsers(); // 사용자 목록 새로고침
        } else {
          notification.error({
            title: "삭제 실패",
            description:
              common.getResultError(result) || "사용자 삭제에 실패했습니다.",
            duration: 3000,
          });
        }
      } catch (err: any) {
        console.error("사용자 삭제 실패:", err);
        notification.error({
          title: "오류",
          description: `사용자 삭제 중 오류 발생: ${err.message}`,
          duration: 3000,
        });
      }
    },
  });
}

async function fetchUsers() {
  listLoading.value = true;
  error.value = null;
  try {
    const result = await getOrgMembersCallable({
      orgId: common.getLocalAuth().orgId,
    });
    orgUsers.value = common.getItemListFromFunctionResult(result) as OrgUser[];
  } catch (err: any) {
    console.error("사용자 목록 로딩 실패:", err);
    error.value = "사용자 목록을 불러오는데 실패했습니다.";
    orgUsers.value = [];
  } finally {
    listLoading.value = false;
  }
}

function viewUserDetails(order: OrgUser) {
  selectedUserDetail.value = order;
  isEditingDetail.value = false;
  editFormData.value = {
    orgId: order.orgId || common.getLocalAuth().orgId,
    uid: order.SK.split("#").pop() || "",
    name: order.name || "",
    email: order.email || "",
    phoneNumber: order.phoneNumber || "",
    role: order.role || "",
    position: order.position || "",
    description: order.description || "",
  };
  showUserDetailModal.value = true;
}

async function handleSaveUserDetail() {
  try {
    await editFormRef.value?.validate();
  } catch (errors) {
    return;
  }

  dialog.warning({
    title: "주의 사항 확인",
    content: "사용자 정보를 수정하더라도 이미 이 사용자가 담당자로 지정된 기존 진단 정보(학급 정보 등)에는 자동으로 반영되지 않습니다.\n기존 진단의 담당자 정보도 함께 업데이트하려면, 해당 진단의 상세 화면에서 '담당자 변경' 기능을 사용하여 재설정해야 합니다.\n\n정말로 이대로 정보를 수정하시겠습니까?",
    positiveText: "예, 수정합니다",
    negativeText: "취소",
    onPositiveClick: async () => {
      isSavingDetail.value = true;
      try {
        const result = await updateOrgUserCallable({
          orgId: editFormData.value.orgId,
          uid: editFormData.value.uid,
          name: editFormData.value.name,
          email: editFormData.value.email || null, // 빈 문자열 대신 null
          phoneNumber: editFormData.value.phoneNumber || null,
          role: editFormData.value.role || null,
          position: editFormData.value.position || null,
          description: editFormData.value.description || null,
        });
        if (common.evalFunctionResult(result)) {
          notification.success({
            title: "성공",
            description: "사용자 정보가 성공적으로 수정되었습니다.",
            duration: 2000,
          });
          showUserDetailModal.value = false;
          fetchUsers(); // 목록 새로고침
        } else {
          notification.error({
            title: "실패",
            description: common.getResultError(result) || "사용자 정보 수정에 실패했습니다.",
            duration: 3000,
          });
        }
      } catch (err: any) {
        console.error("사용자 정보 수정 에러:", err);
        notification.error({
          title: "에러",
          description: "오류가 발생했습니다.",
          duration: 3000,
        });
      } finally {
        isSavingDetail.value = false;
      }
    }
  });
}


function changePassword(user: OrgUser) {
  userForPasswordChange.value = user;
  passwordFormData.value.oldPassword = "";
  passwordFormData.value.newPassword = "";
  passwordFormData.value.confirmNewPassword = "";
  passwordChangeError.value = null;
  showChangePasswordModal.value = true;
}

async function handlePasswordChangeSubmit() {
  if (!userForPasswordChange.value) return;

  passwordChangeError.value = null;
  if (!passwordFormData.value.newPassword) {
    passwordChangeError.value = "새 암호를 입력해주세요.";
    message.error(passwordChangeError.value);
    return;
  }
  if (
    passwordFormData.value.newPassword !==
    passwordFormData.value.confirmNewPassword
  ) {
    passwordChangeError.value = "새 암호와 암호 확인이 일치하지 않습니다.";
    message.error(passwordChangeError.value);
    return;
  }
  passwordChangeLoading.value = true;
  try {
    const userId = userForPasswordChange.value.SK.split("#").pop();
    const orgId = common.getLocalAuth().orgId;
    const param: ChangePassword = {
      orgId: orgId,
      uid: userId,
      old_password: passwordFormData.value.oldPassword,
      new_password: passwordFormData.value.newPassword,
    };
    const result = await changeOrgMemberPasswordCallable({
      orgId: orgId,
      uid: userId,
      old_password: passwordFormData.value.oldPassword,
      new_password: passwordFormData.value.newPassword,
    });
    if (common.evalFunctionResult(result)) {
      notification.success({
        title: "성공",
        description: "암호가 성공적으로 변경되었습니다.",
        duration: 2000,
      });
    } else {
      notification.error({
        title: "실패",
        description:
          common.getResultError(result) || "암호 변경에 실패했습니다.",
        duration: 2000,
      });
    }
    showChangePasswordModal.value = false;
  } catch (err: any) {
    console.error("암호 변경 실패:", err);
    passwordChangeError.value = `암호 변경에 실패했습니다: ${
      err.message || "알 수 없는 오류"
    }`;
  } finally {
    passwordChangeLoading.value = false;
  }
}

function openAddUserModal() {
  onCloseAddUserModal(); // 이전 데이터 초기화
  showAddUserModal.value = true;
}

async function handleAddUserSubmit() {
  addUserError.value = null;
  try {
    await addUserFormRef.value?.validate();
  } catch (errors) {
    return;
  }

  addUserLoading.value = true;
  try {
    const orgId = common.getLocalAuth().orgId;
    const userDataToAdd = {
      orgId: orgId,
      uid: newUserData.value.uid,
      password: newUserData.value.password,
      name: newUserData.value.name,
      email: newUserData.value.email || null, // 빈 문자열 대신 null
      phoneNumber: newUserData.value.phoneNumber || null,
      role: newUserData.value.role || null,
      position: newUserData.value.position || null,
      description: newUserData.value.description || null,
      type: newUserData.value.type,
    };

    const result = await addOrgMemberCallable(userDataToAdd);

    if (common.evalFunctionResult(result)) {
      notification.success({
        title: "성공",
        description: "사용자가 성공적으로 추가되었습니다.",
        duration: 2000,
      });
      showAddUserModal.value = false;
      fetchUsers(); // 사용자 목록 새로고침
    } else {
      addUserError.value =
        common.getResultError(result) || "사용자 추가에 실패했습니다.";
      notification.error({
        title: "실패",
        description: addUserError.value,
        duration: 3000,
      });
    }
  } catch (err: any) {
    console.error("사용자 추가 실패:", err);
    addUserError.value = `사용자 추가 중 오류가 발생했습니다: ${
      err.message || "알 수 없는 오류"
    }`;
    notification.error({
      title: "오류",
      description: addUserError.value,
      duration: 3000,
    });
  } finally {
    addUserLoading.value = false;
  }
}

function onCloseChangePasswordModal() {
  userForPasswordChange.value = null;
  passwordFormData.value.newPassword = "";
  passwordFormData.value.confirmNewPassword = "";
  passwordChangeError.value = null;
}
function onCloseAddUserModal() {
  newUserData.value = {
    id: "",
    name: "",
    email: "",
    phoneNumber: "",
    role: "",
    position: "",
    password: "",
    confirmPassword: "",
    type: "",
  };
  addUserError.value = null;
  addUserFormRef.value?.restoreValidation();
}

onMounted(async () => {
  const localAuthData = common.getLocalAuth();
  if (localAuthData) {
    if (localAuthData.org && typeof localAuthData.org === "object") {
      organizationInfo.value = localAuthData.org as Partial<Organization>;
      organizationInfo.value.uid = localAuthData.orgId;
      fetchUsers();
    } else {
      await useAlert("기관 회원전용 페이지 입니다. 로그인 후 이용해주세요.");
      router.push({ name: USER_ROUTES.Home.Name });
    }
  } else {
    await useAlert("회원전용 페이지 입니다. 로그인 후 이용해주세요.");
    router.push({ name: USER_ROUTES.Home.Name });
  }
});
</script>

<style scoped>
.product-item-card-modal {
  border: 1px solid #eee;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 4px;
  background-color: #f9f9f9;
}
.sub_title_01 p {
  position: absolute;
  top: 70px;
  left: 0;
  margin-top: -15px;

  width: 100%;
  font-size: 30px;
  color: #fff;
  text-align: center;
  font-family: "Noto Sans", "Nanum Gothic", "Malgun Gothic", sans-serif;
  font-weight: 600;
}
.sub_title_01 h2 {
  position: absolute;
  top: 130px;
  left: 0;
  margin-top: -15px;

  width: 100%;
  font-size: 19px;
  color: #fff;
  text-align: center;
  font-family: "Noto Sans", "Nanum Gothic", "Malgun Gothic", sans-serif;
  font-weight: 500;
  line-height: 1.7em;
}

.sub_scroll_wrap {
  overflow: hidden;
  height: 39px;
  border-bottom: 1px solid #ccc;
  background: #fff;
}
.sub_scroll_wrap > div {
  position: relative; /*padding: 0 30px;*/
}
.sub_scroll_wrap > div > div {
  overflow-x: scroll;
}
.sub_scroll_wrap > div > div .scroll_nav {
  display: inline-block;
  min-width: 100%;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  list-style: none;
  font-size: 0;
  text-align: center;
  white-space: nowrap;
}
.sub_scroll_wrap > div > div .scroll_nav li {
  display: inline-block;
}
.sub_scroll_wrap > div > div .scroll_nav li a {
  display: inline-block;
  height: 39px;
  line-height: 39px;
  padding: 0 12px;
  font-size: 13px;
  color: #bbb;
  font-family: "Montserrat", "Noto Sans", NanumGothic, "나눔고딕", "Open Sans",
    sans-serif;
  box-sizing: border-box;
  font-weight: 600;
}
.sub_scroll_wrap > div > div .scroll_nav li.on a {
  color: #be7b42;
  font-weight: 600;
}
.sub_scroll_wrap > div i {
  position: absolute;
  left: 0;
  top: 0;
  width: 30px;
  height: 39px;
}
.sub_scroll_wrap > div i.prev:before {
  content: "";
  position: absolute;
  top: 0;
  left: 3px;
  bottom: 0;
  width: 0;
  height: 0;
  margin: auto;
  border: 6px solid transparent;
  border-right-color: #333;
}
.sub_scroll_wrap > div i.next {
  right: 0;
  left: inherit;
}
.sub_scroll_wrap > div i.next:before {
  content: "";
  position: absolute;
  top: 0;
  right: 3px;
  bottom: 0;
  width: 0;
  height: 0;
  margin: auto;
  border: 6px solid transparent;
  border-left-color: #333;
}
@media screen and (min-width: 768px) {
  .sub_scroll_wrap > div i {
    display: none;
  }
}
@media screen and (min-width: 1200px) {
  .sub_scroll_wrap {
    overflow: visible;
    height: auto;
    border-bottom: none;
    background: #fff;
    box-shadow: 3px 3px 3px rgb(0 0 0 / 10%);
  }
  .sub_scroll_wrap > div {
    position: relative;
    padding: 0;
  }
  .sub_scroll_wrap > div > div {
    overflow: hidden;
  }
  .sub_scroll_wrap > div > div .scroll_nav {
    display: table;
    margin: 0;
    width: 100%;
    padding: 0;
    white-space: normal;
    text-align: left;
    border-collapse: collapse;
  }
  .sub_scroll_wrap > div > div .scroll_nav li {
    display: table-cell;
    width: 33%;
    position: relative;
  }
  .sub_scroll_wrap > div > div .scroll_nav li:after {
    content: "";
    display: block;
    position: absolute;
    top: 50%;
    right: 0;
    background: #bbb;
    width: 1px;
    height: 15px;
    margin-top: -7px;
  }
  .sub_scroll_wrap > div > div .scroll_nav li:last-child:after {
    display: none;
  }
  .sub_scroll_wrap > div > div .scroll_nav li:before {
    position: absolute;
    visibility: hidden;
    left: 0;
    bottom: 0px;
    right: 0px;
    width: 100%;
    height: 3px;
    background: #be7b42;
    content: "";
    -webkit-transform: scaleX(0);
    transform: scaleX(0);
    -webkit-transition: all 0.2s ease-in-out 0s;
    transition: all 0.2s ease-in-out 0s;
    opacity: 0.85;
  }
  .sub_scroll_wrap > div > div .scroll_nav li a {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    height: 60px;
    line-height: 60px;
    padding: 0 5px;
    font-size: 16px;
    text-align: center;
    color: #bbbbbb;
    font-weight: 600;
  }
  .sub_scroll_wrap > div > div .scroll_nav li a:hover {
    color: #be7b42;
  }
  .sub_scroll_wrap > div > div .scroll_nav li.on a {
    color: #be7b42;
  }
  .sub_scroll_wrap > div > div .scroll_nav li.on:before,
  .sub_scroll_wrap > div > div .scroll_nav li:hover:before {
    visibility: visible;
    -webkit-transform: scaleX(1);
    transform: scaleX(1);
  }
}

.sub04_wrap * {
  font-family: "Montserrat", "Noto Sans", NanumGothic, "나눔고딕", "Open Sans",
    sans-serif;
  box-sizing: border-box;
  word-break: keep-all;
}
.sub04_wrap > ul {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}
.sub04_wrap > ul li {
  width: 48%;
}
.sub04_wrap > ul li figure {
  border: 1px solid #ddd;
}
.sub04_wrap > ul li:nth-child(-n + 2) {
  margin-bottom: 30px;
}
.sub04_wrap > ul li a {
  display: block;
  border-radius: 10px;
  position: relative;
  transition: all.2s;
}
.sub04_wrap > ul li h1 {
  font-size: 14px;
  font-weight: 500;
  padding-top: 15px;
  text-align: center;
}
.sub04_wrap > ul li a > div.bg {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: none;
  opacity: 0;
  transition: all.2s;
}
.sub04_wrap > ul li a > div.bg > span {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  line-height: 80px;
  text-align: center;
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  top: 50%;
  background: #ba7b3d;
  color: #fff;
  font-weight: 500;
}
@media screen and (min-width: 768px) {
  .sub04_wrap > ul {
    justify-content: normal;
  }
  .sub04_wrap > ul li h1 {
    font-size: 16px;
  }
  .sub04_wrap > ul li {
    width: 23.5%;
    margin-right: 2%;
  }
  .sub04_wrap > ul li:nth-child(-n + 2) {
    margin-bottom: 0px;
  }
  .sub04_wrap > ul li:last-child {
    margin-right: 0;
  }
  .sub04_wrap > ul li a:hover > div.bg {
    display: block;
    opacity: 1;
  }
}

@media all and (min-width: 768px) {
  .glores-B-list-h2 .glores-B-full-select-btn select[name="myreg_idx"] {
    width: auto;
    min-width: 250px;
  }
}
</style>
